"use client";
import { Vehicle, VehicleStatusType } from "@alba-cars/common-modules";
import { useTranslations } from "next-intl";
import React, { useMemo, useState } from "react";

import TestDriveModal from "./TestDriveModal";
import { roundNumberTo } from "@/lib/utils";
import { isReservedConsignment } from "@/lib/vehicle-ownership";

interface LoanSummaryCardProps {
  totalPrice: number;
  downPaymentPercentage: number;
  tenure: number;
  interestRate: number;
  car: Vehicle;
  onStartFinancing?: () => void;
}

const LoanSummaryCard: React.FC<LoanSummaryCardProps> = ({
  totalPrice,
  downPaymentPercentage,
  tenure,
  interestRate,
  car,
  onStartFinancing,
}) => {
  const t = useTranslations("CarDetails.loanSummary");

  const formatCurrency = (value: number): string => {
    return `AED ${Math.round(value).toLocaleString("en-US")}`;
  };

  const calculations = useMemo(() => {
    // Calculate down payment amount
    const downPaymentAmount = (totalPrice * downPaymentPercentage) / 100;

    // Calculate loan amount
    const loanAmount = totalPrice - downPaymentAmount;

    // Calculate EMI
    // Flat EMI Formula: EMI = (P + (P * I * T)/100) / (T * 12)
    // where P = Principal (loan amount)
    // I = Interest rate per month
    // T = Tenure in years
    // const monthlyInterestRate = interestRate / 12 / 100;
    const totalMonths = tenure * 12;
    const totalInterest = (loanAmount * interestRate * tenure) / 100;

    const emi = (loanAmount + totalInterest) / totalMonths;
    // const emi =
    //   (loanAmount * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, totalMonths)) /
    //   (Math.pow(1 + monthlyInterestRate, totalMonths) - 1);

    // Calculate total payment and total interest
    const totalPayment = emi * totalMonths;
    // const totalInterest = totalPayment - loanAmount;

    return {
      downPaymentAmount,
      loanAmount,
      emi,
      totalPayment,
      totalInterest,
    };
  }, [totalPrice, downPaymentPercentage, tenure, interestRate]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const isSold = car.status === VehicleStatusType.SOLD;
  const buttonDisabled = isSold || isReservedConsignment(car);

  return (
    <div className="flex flex-col justify-between rounded-3xl bg-secondary-light p-5">
      <div className="flex flex-col items-center">
        <h4 className="font-inter text-sm text-secondary-foreground">{t("indicativeEMI")}</h4>
        <span className="font-urbanist text-3xl font-bold">{formatCurrency(calculations.emi)}</span>
        <span className="font-inter text-sm text-secondary-foreground">{t("forYears", { count: tenure })}</span>
      </div>

      <div className="mt-4 flex justify-between px-5">
        <div className="flex flex-col space-y-1">
          <span className="font-inter text-sm text-secondary-foreground">{t("downPayment")}</span>
          <span className="font-urbanist text-base font-bold">{formatCurrency(calculations.downPaymentAmount)}</span>
        </div>
        <div className="flex flex-col space-y-1">
          <span className="font-inter text-sm text-secondary-foreground">{t("loanAmount")}</span>
          <span className="font-urbanist text-base font-bold">{formatCurrency(calculations.loanAmount)}</span>
        </div>
      </div>

      <div className="mt-3 flex justify-between px-5">
        <div className="flex flex-col space-y-1">
          <span className="font-inter text-sm text-secondary-foreground">{t("interestRate")}</span>
          <span className="font-urbanist text-base font-bold">{roundNumberTo(interestRate, 2)}%</span>
        </div>
        {/* <div className="flex flex-col space-y-1">
          <span className="font-inter text-sm text-secondary-foreground">Total Interest</span>
          <span className="font-urbanist text-base font-bold">{formatCurrency(calculations.totalInterest)}</span>
        </div> */}
      </div>

      <div className="mt-4">
        <button
          disabled={buttonDisabled}
          onClick={() => {
            if (buttonDisabled) {
              return;
            }

            setIsModalOpen(true);
          }}
          className="w-full rounded-xl bg-primary py-4 text-center font-inter font-medium text-white transition-colors hover:cursor-pointer hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t("bookTestDrive")}
        </button>
        <TestDriveModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} vehicleId={car.id} />
        {/* <TestDriveButton car={ca}/> */}
      </div>
    </div>
  );
};

export default LoanSummaryCard;
